/*
 * A workbook, read off its own XML. An `.xlsx` is a zip of XML parts, and the three that
 * matter here are the list of sheets, the shared strings every text cell points into, and
 * each sheet's rows of cells. Nothing else is read — not the styles, not the formats — which
 * is why a date arrives as the number Excel keeps it as and a time as a fraction of a day:
 * whoever reads the sheet knows which columns those are, and this does not have to.
 */

import { strFromU8, unzipSync } from 'fflate'

/** What a cell holds: a number as written, text as written, or nothing. */
export type Cell = string | number | null

export interface Sheet {
  name: string
  /** Every row from the first, each as long as its last filled cell. */
  rows: Cell[][]
}

export interface Workbook {
  sheets: Sheet[]
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
}

/** The five named entities and the numeric ones, which is all an XML writer may use. */
export function unescapeXml(text: string): string {
  return text.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-z]+);/g, (whole, entity: string) => {
    if (entity.startsWith('#x')) {
      return String.fromCodePoint(parseInt(entity.slice(2), 16))
    }
    if (entity.startsWith('#')) {
      return String.fromCodePoint(parseInt(entity.slice(1), 10))
    }
    return ENTITIES[entity] ?? whole
  })
}

/** Every `<t>` inside one string item, joined — a string with several runs is several. */
function textOf(item: string): string {
  let text = ''
  for (const run of item.matchAll(/<t(?:\s[^>]*)?>([^<]*)<\/t>/g)) {
    text += unescapeXml(run[1] ?? '')
  }
  return text
}

function sharedStringsOf(xml: string | undefined): string[] {
  if (xml === undefined) {
    return []
  }
  const strings: string[] = []
  for (const item of xml.matchAll(/<si>([\s\S]*?)<\/si>/g)) {
    strings.push(textOf(item[1] ?? ''))
  }
  return strings
}

/** `AB` → 27, the way a cell's reference counts its column, from zero here. */
export function columnOf(reference: string): number {
  let column = 0
  for (const letter of reference) {
    if (letter < 'A' || letter > 'Z') {
      break
    }
    column = column * 26 + (letter.charCodeAt(0) - 64)
  }
  return column - 1
}

function rowsOf(xml: string, strings: readonly string[]): Cell[][] {
  const rows: Cell[][] = []
  for (const row of xml.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    const cells: Cell[] = []
    for (const cell of (row[1] ?? '').matchAll(/<c\s([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attributes = cell[1] ?? ''
      const inner = cell[2] ?? ''
      const reference = /r="([A-Z]+)\d*"/.exec(attributes)?.[1]
      if (reference === undefined) {
        continue
      }
      const at = columnOf(reference)
      const type = /\bt="([^"]*)"/.exec(attributes)?.[1] ?? 'n'
      const value = /<v>([^<]*)<\/v>/.exec(inner)?.[1]
      let held: Cell = null
      if (type === 's') {
        held = value === undefined ? null : (strings[Number(value)] ?? null)
      } else if (type === 'inlineStr') {
        held = textOf(inner)
      } else if (type === 'str') {
        held = value === undefined ? null : unescapeXml(value)
      } else if (type === 'b') {
        held = value === '1' ? 'TRUE' : 'FALSE'
      } else if (value !== undefined && value !== '') {
        const number = Number(value)
        held = Number.isFinite(number) ? number : unescapeXml(value)
      }
      if (held === '') {
        held = null
      }
      while (cells.length < at) {
        cells.push(null)
      }
      cells[at] = held
    }
    while (cells.length > 0 && cells[cells.length - 1] === null) {
      cells.pop()
    }
    rows.push(cells)
  }
  return rows
}

/**
 * The workbook as its sheets, in the order the workbook lists them. Throws on anything that
 * is not a workbook at all — no zip, or a zip with no sheet list in it.
 */
export function readWorkbook(bytes: Uint8Array): Workbook {
  const parts = unzipSync(bytes)
  const part = (name: string): string | undefined => {
    const found = parts[name]
    return found === undefined ? undefined : strFromU8(found)
  }
  const workbook = part('xl/workbook.xml')
  if (workbook === undefined) {
    throw new Error('not a workbook')
  }
  const relations = new Map<string, string>()
  for (const relation of (part('xl/_rels/workbook.xml.rels') ?? '').matchAll(
    /<Relationship\s([^>]*)\/?>/g,
  )) {
    const attributes = relation[1] ?? ''
    const id = /\bId="([^"]*)"/.exec(attributes)?.[1]
    const target = /\bTarget="([^"]*)"/.exec(attributes)?.[1]
    if (id !== undefined && target !== undefined) {
      relations.set(id, target.startsWith('/') ? target.slice(1) : `xl/${target}`)
    }
  }
  const strings = sharedStringsOf(part('xl/sharedStrings.xml'))
  const sheets: Sheet[] = []
  for (const sheet of workbook.matchAll(/<sheet\s([^>]*)\/?>/g)) {
    const attributes = sheet[1] ?? ''
    const name = /\bname="([^"]*)"/.exec(attributes)?.[1]
    const id = /\br:id="([^"]*)"/.exec(attributes)?.[1]
    const target = id === undefined ? undefined : relations.get(id)
    const xml = target === undefined ? undefined : part(target)
    if (name === undefined || xml === undefined) {
      continue
    }
    sheets.push({ name: unescapeXml(name), rows: rowsOf(xml, strings) })
  }
  return { sheets }
}
